import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  LinearProgress,
  Chip,
  Skeleton,
  Grid
} from '@mui/material';
import {
  EmojiEvents,
  Group,
  TrendingUp,
  Launch
} from '@mui/icons-material';

const CurrentChallenge = ({ challenge, loading, onViewChallenge, onViewLeaderboard }) => {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="rectangular" height={60} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  if (!challenge) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <EmojiEvents sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Not in a Challenge
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Join a challenge to compete with others and track your progress!
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.href = '/challenges'}
          >
            Browse Challenges
          </Button>
        </CardContent>
      </Card>
    );
  }

  const progressPercentage = (challenge.userProgress / challenge.goal) * 100;
  const daysLeft = challenge.daysLeft || 0;

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Current Challenge
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {challenge.name}
            </Typography>
          </Box>
          <Chip 
            label={`${daysLeft} days left`} 
            color={daysLeft <= 7 ? 'warning' : 'primary'}
            size="small"
          />
        </Box>

        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2">
              Your Progress
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {challenge.userProgress?.toLocaleString() || 0} / {challenge.goal?.toLocaleString() || 0} steps
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={Math.min(progressPercentage, 100)} 
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {progressPercentage.toFixed(1)}% complete
          </Typography>
        </Box>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={4}>
            <Box textAlign="center">
              <Typography variant="h6" fontWeight="bold">
                {challenge.participants || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Participants
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box textAlign="center">
              <Typography variant="h6" fontWeight="bold">
                #{challenge.userRank || '-'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Your Rank
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box textAlign="center">
              <Typography variant="h6" fontWeight="bold">
                {challenge.totalSteps?.toLocaleString() || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Steps
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Launch />}
            onClick={() => onViewChallenge(challenge.id)}
            fullWidth
          >
            View Challenge
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<TrendingUp />}
            onClick={() => onViewLeaderboard(challenge.id)}
            fullWidth
          >
            Leaderboard
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CurrentChallenge;